import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import MakeupShopNav from '../components/MakeupShopNav';
import { artistGridApi } from '../api';
import { SCHEDULE_CATEGORY_COLORS, SCHEDULE_CATEGORIES } from '../constants';

interface Artist {
  userIdx: number;
  name: string;
}

interface Schedule {
  makeupScheduleIdx: number;
  scheduleCategory: string;
  section: string;
  title: string;
  startDt: string;
  endDt: string;
  status: string;
  groomName: string;
  brideName: string;
  customerPhone: string;
  honjuName: string;
  venue: string;
  makeupStaff?: { userIdx: number; name: string };
  hairStaff?: { userIdx: number; name: string };
  makeupCustomer?: { makeupCustomerIdx: number };
}

export default function ArtistGrid() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offset).toISOString().split('T')[0];
  });
  const [artists, setArtists] = useState<Artist[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);

  const fetchGrid = useCallback(async () => {
    setLoading(true);
    try {
      const data = await artistGridApi.grid(selectedDate);
      setArtists(data.artists || []);
      setSchedules(data.schedules || []);
      setTimeSlots(data.timeSlots || []);
    } catch (error) {
      console.error('아티스트 그리드 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchGrid();
  }, [fetchGrid]);

  const moveDate = (days: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    const offset = d.getTimezoneOffset() * 60000;
    setSelectedDate(new Date(d.getTime() - offset).toISOString().split('T')[0]);
  };

  const goToday = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    setSelectedDate(new Date(now.getTime() - offset).toISOString().split('T')[0]);
  };

  const getHour = (timeStr: string) => {
    const [h, m] = timeStr.split(':').map(Number);
    return h + m / 60;
  };

  const parseTime = (dt: string): string => {
    if (!dt) return '00:00';
    const d = new Date(dt);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  // 특정 아티스트+시간 슬롯에 해당하는 스케줄 찾기
  const getScheduleInfo = (artistIdx: number, slotTime: string) => {
    const slotHour = getHour(slotTime);

    const schedule = schedules.find((s) => {
      const isAssigned =
        s.makeupStaff?.userIdx === artistIdx || s.hairStaff?.userIdx === artistIdx;
      if (!isAssigned) return false;

      const startH = getHour(parseTime(s.startDt));
      const endH = getHour(parseTime(s.endDt));
      return slotHour >= startH && slotHour < endH;
    });

    if (!schedule) return { type: 'none' };

    const startH = getHour(parseTime(schedule.startDt));
    const endH = getHour(parseTime(schedule.endDt));
    const duration = Math.round((endH - startH) * 2); // 30분 단위

    if (slotHour === startH) {
      const role =
        schedule.makeupStaff?.userIdx === artistIdx ? '메이크업' : '헤어';
      return { type: 'start', schedule, span: duration, role };
    }
    return { type: 'middle' };
  };

  const getCategoryLabel = (value: string) =>
    SCHEDULE_CATEGORIES.find((c) => c.value === value)?.label || value;

  const getCategoryBgColor = (value: string) => {
    const map: Record<string, string> = {
      TASK: 'bg-cyan-50 border-cyan-400',
      REHEARSAL: 'bg-blue-50 border-blue-400',
      MAIN_CEREMONY: 'bg-orange-50 border-orange-400',
      CONSULTATION: 'bg-green-50 border-green-400',
      PAYMENT: 'bg-lime-50 border-lime-400',
    };
    return map[value] || 'bg-gray-50 border-gray-400';
  };

  const getCustomerLabel = (s: Schedule) =>
    [s.brideName, s.groomName].filter(Boolean).join(' & ') || s.honjuName || '-';

  const dateStr = new Date(selectedDate + 'T00:00:00').toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  });

  // 얼리스타트 표시 (06:00 이전 스케줄)
  const earlyStartSchedules = schedules.filter((s) => {
    const h = getHour(parseTime(s.startDt));
    return h < 6 && s.scheduleCategory === 'MAIN_CEREMONY';
  });

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <MakeupShopNav />

          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">아티스트 현황</h1>
              <p className="text-sm text-gray-500 mt-1">
                아티스트별 일정 배정 현황을 한눈에 확인합니다
              </p>
            </div>
          </div>

          {/* 날짜 네비게이션 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => moveDate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <i className="ri-arrow-left-s-line text-xl text-gray-600"></i>
                </button>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button onClick={() => moveDate(1)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <i className="ri-arrow-right-s-line text-xl text-gray-600"></i>
                </button>
                <button
                  onClick={goToday}
                  className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
                >
                  오늘
                </button>
              </div>
              <div className="text-lg font-semibold text-gray-800">{dateStr}</div>
              <div className="flex items-center gap-3 text-xs">
                {SCHEDULE_CATEGORIES.filter((c) => c.value !== 'TASK' && c.value !== 'PAYMENT').map(
                  (cat) => (
                    <span key={cat.value} className="flex items-center gap-1">
                      <span className={`w-3 h-3 rounded-full ${cat.color}`}></span>
                      {cat.label}
                    </span>
                  ),
                )}
                {earlyStartSchedules.length > 0 && (
                  <span className="flex items-center gap-1 text-red-600 font-medium">
                    <i className="ri-alarm-warning-line"></i>
                    얼리스타트 {earlyStartSchedules.length}건
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* 그리드 */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : artists.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <i className="ri-team-line text-5xl text-gray-300 mb-4 block"></i>
              <p className="text-gray-500">등록된 아티스트가 없습니다.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="border-b border-r border-gray-200 bg-gray-50 px-3 py-3 text-left text-sm font-semibold text-gray-700 sticky left-0 z-10 min-w-[70px]">
                        시간
                      </th>
                      {artists.map((artist) => (
                        <th
                          key={artist.userIdx}
                          className="border-b border-r border-gray-200 bg-gray-50 px-3 py-3 text-center text-sm font-semibold text-gray-700 min-w-[160px]"
                        >
                          <i className="ri-user-line mr-1"></i>
                          {artist.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map((slot) => {
                      const hour = parseInt(slot.split(':')[0]);
                      const isEarlyHour = hour < 6;
                      return (
                        <tr key={slot}>
                          <td
                            className={`border-b border-r border-gray-200 px-3 py-2 text-xs font-mono font-medium sticky left-0 z-10 ${
                              isEarlyHour
                                ? 'bg-red-50 text-red-600'
                                : 'bg-gray-50 text-gray-600'
                            }`}
                          >
                            {slot}
                            {isEarlyHour && slot.endsWith(':00') && (
                              <span className="ml-1 text-red-400" title="얼리스타트 시간대">
                                *
                              </span>
                            )}
                          </td>
                          {artists.map((artist) => {
                            const info = getScheduleInfo(artist.userIdx, slot);
                            if (info.type === 'middle') return null;
                            if (info.type === 'start' && info.schedule) {
                              const s = info.schedule;
                              const bgColor = getCategoryBgColor(s.scheduleCategory);
                              const startTime = parseTime(s.startDt);
                              const endTime = parseTime(s.endDt);
                              const isEarly =
                                getHour(startTime) < 6 &&
                                s.scheduleCategory === 'MAIN_CEREMONY';

                              return (
                                <td
                                  key={artist.userIdx}
                                  rowSpan={info.span}
                                  className="border-b border-r border-gray-200 p-1 align-top relative cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => setSelectedSchedule(s)}
                                >
                                  <div
                                    className={`${bgColor} border-l-4 rounded-r p-2 h-full absolute inset-1 overflow-hidden shadow-sm`}
                                  >
                                    {isEarly && (
                                      <span className="text-[10px] bg-red-500 text-white px-1 rounded mb-1 inline-block">
                                        얼리
                                      </span>
                                    )}
                                    <p className="font-bold text-gray-800 text-xs truncate">
                                      {getCustomerLabel(s)}
                                    </p>
                                    <p className="text-[10px] text-gray-600 mt-0.5">
                                      {startTime}~{endTime}
                                    </p>
                                    <p className="text-[10px] text-gray-500 truncate">
                                      {getCategoryLabel(s.scheduleCategory)} · {info.role}
                                    </p>
                                    {s.venue && (
                                      <p className="text-[10px] text-gray-400 truncate mt-0.5">
                                        <i className="ri-map-pin-line mr-0.5"></i>
                                        {s.venue}
                                      </p>
                                    )}
                                  </div>
                                </td>
                              );
                            }
                            return (
                              <td
                                key={artist.userIdx}
                                className="border-b border-r border-gray-200 p-1 h-8"
                              ></td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* 통계 */}
              <div className="border-t border-gray-200 bg-gray-50 px-6 py-3 flex items-center gap-6 text-sm text-gray-600">
                <span>
                  <i className="ri-team-line mr-1"></i>아티스트 {artists.length}명
                </span>
                <span>
                  <i className="ri-calendar-check-line mr-1"></i>일정 {schedules.length}건
                </span>
                {earlyStartSchedules.length > 0 && (
                  <span className="text-red-600">
                    <i className="ri-alarm-warning-line mr-1"></i>얼리스타트{' '}
                    {earlyStartSchedules.length}건
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 스케줄 상세 모달 */}
      {selectedSchedule && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="bg-blue-600 px-6 py-4 flex justify-between items-center">
              <h3 className="text-white font-bold text-lg">일정 상세</h3>
              <button
                onClick={() => setSelectedSchedule(null)}
                className="text-white hover:bg-blue-700 rounded-full p-1"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6 space-y-3 text-sm">
              <div className="flex items-center gap-2 pb-3 border-b">
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                    SCHEDULE_CATEGORY_COLORS[selectedSchedule.scheduleCategory] || ''
                  }`}
                >
                  {getCategoryLabel(selectedSchedule.scheduleCategory)}
                </span>
                <span className="text-xs text-gray-400">{selectedSchedule.section}</span>
                {getHour(parseTime(selectedSchedule.startDt)) < 6 &&
                  selectedSchedule.scheduleCategory === 'MAIN_CEREMONY' && (
                    <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">
                      얼리스타트
                    </span>
                  )}
              </div>
              <div className="flex">
                <span className="w-20 text-gray-500">고객</span>
                <span className="font-medium">{getCustomerLabel(selectedSchedule)}</span>
              </div>
              <div className="flex">
                <span className="w-20 text-gray-500">시간</span>
                <span className="font-medium">
                  {parseTime(selectedSchedule.startDt)} ~ {parseTime(selectedSchedule.endDt)}
                </span>
              </div>
              {selectedSchedule.venue && (
                <div className="flex">
                  <span className="w-20 text-gray-500">장소</span>
                  <span>{selectedSchedule.venue}</span>
                </div>
              )}
              {selectedSchedule.makeupStaff && (
                <div className="flex">
                  <span className="w-20 text-gray-500">메이크업</span>
                  <span>{selectedSchedule.makeupStaff.name}</span>
                </div>
              )}
              {selectedSchedule.hairStaff && (
                <div className="flex">
                  <span className="w-20 text-gray-500">헤어</span>
                  <span>{selectedSchedule.hairStaff.name}</span>
                </div>
              )}
              {selectedSchedule.honjuName && (
                <div className="flex">
                  <span className="w-20 text-gray-500">혼주</span>
                  <span>{selectedSchedule.honjuName}</span>
                </div>
              )}
              <div className="pt-3 border-t flex gap-2">
                {selectedSchedule.makeupCustomer?.makeupCustomerIdx && (
                  <button
                    onClick={() => {
                      navigate(
                        `/makeup-shop/customer-detail?id=${selectedSchedule.makeupCustomer!.makeupCustomerIdx}`,
                      );
                      setSelectedSchedule(null);
                    }}
                    className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    고객상세
                  </button>
                )}
                <button
                  onClick={() => setSelectedSchedule(null)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
